import { useState } from 'react';
import { useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button, Card, Text, Spinner, Textarea, Field,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  makeStyles, tokens,
} from '@fluentui/react-components';
import {
  fetchPendingExpenses, approveExpense, rejectExpense, type PendingExpense,
} from '../api/expenses';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { usePageTitle } from '../hooks/usePageTitle';
import { PageTitle } from '../components/shared/PageTitle';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL, maxWidth: '960px', margin: '0 auto' },
  card: { padding: tokens.spacingVerticalM, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  info: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '200px', flex: 1 },
  muted: { color: tokens.colorNeutralForeground3, fontSize: '12px' },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS },
  dangerText: { color: tokens.colorStatusDangerForeground1, fontSize: '12px', display: 'block', marginTop: '8px' },
});

/** 409 = otra decisión ya resolvió el gasto; cualquier otro error usa el mensaje genérico. */
function isConflict(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 409;
}

/**
 * Bandeja "Por aprobar" (B2). Solo se monta bajo la ruta protegida por
 * `expenses:approve`; el API vuelve a exigir el permiso en cada acción.
 */
export function ExpenseApprovalsPage() {
  const s = useStyles();
  const t = useT();
  const { fmtNum, fmtDateOnly } = useIntlFormat();
  const qc = useQueryClient();
  const { tenantId } = useParams<{ tenantId: string }>();
  usePageTitle(t.expenseApprovals.title);

  const [rejectTarget, setRejectTarget] = useState<PendingExpense | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const { data: pending = [], isLoading } = useQuery({
    queryKey: ['pendingExpenses', tenantId],
    queryFn: () => fetchPendingExpenses(tenantId!),
    enabled: !!tenantId,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['pendingExpenses', tenantId] });
  const onActionError = (err: unknown) => {
    setError(isConflict(err) ? t.expenseApprovals.conflict : t.expenseApprovals.actionError);
    if (isConflict(err)) refresh();
  };

  const approveMut = useMutation({
    mutationFn: (expenseId: string) => approveExpense(tenantId!, expenseId),
    onSuccess: () => { setError(''); refresh(); },
    onError: onActionError,
  });

  const rejectMut = useMutation({
    mutationFn: ({ expenseId, why }: { expenseId: string; why: string }) => rejectExpense(tenantId!, expenseId, why || undefined),
    onSuccess: () => { setRejectTarget(null); setReason(''); setError(''); refresh(); },
    onError: (err) => { setRejectTarget(null); setReason(''); onActionError(err); },
  });

  const scopeLabel = (e: PendingExpense) =>
    e.scope === 'TENANT' ? t.expenseApprovals.scopeTenant
      : e.scope === 'RANCHO' ? t.expenseApprovals.scopeRancho
        : t.expenseApprovals.scopeLote;

  const money = (amount: number, currency: string) =>
    `${currency} ${fmtNum(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className={s.page}>
      <div>
        <PageTitle>{t.expenseApprovals.title}</PageTitle>
        <Text className={s.muted} block>{t.expenseApprovals.subtitle}</Text>
      </div>

      {error && <Text className={s.dangerText} role="alert">{error}</Text>}

      {isLoading ? (
        <Spinner size="small" label={t.expenseApprovals.loading} />
      ) : pending.length === 0 ? (
        <Text className={s.muted}>{t.expenseApprovals.empty}</Text>
      ) : (
        pending.map(e => (
          <Card key={e.expenseId} className={s.card} data-testid="pending-expense-row">
            <div className={s.info}>
              <Text weight="semibold">{e.description}</Text>
              <Text className={s.muted}>
                {fmtDateOnly(e.occurredAt)} · {e.category}
                {e.counterpartyName ? ` · ${e.counterpartyName}` : ''}
              </Text>
              <Text className={s.muted}>
                {t.expenseApprovals.colScope}: {scopeLabel(e)} · {t.expenseApprovals.colRegisteredBy}: {e.createdByName ?? e.createdBy}
              </Text>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Text weight="semibold" block>{t.expenseApprovals.colAmount}: {money(e.totalAmount, e.currency)}</Text>
              {e.currency !== e.baseCurrency && (
                <Text className={s.muted} block>{money(e.baseTotal, e.baseCurrency)}</Text>
              )}
            </div>
            <div className={s.actions}>
              <Button
                appearance="primary" size="small"
                disabled={approveMut.isPending || rejectMut.isPending}
                onClick={() => { setError(''); approveMut.mutate(e.expenseId); }}
              >
                {t.expenseApprovals.approve}
              </Button>
              <Button
                appearance="subtle" size="small"
                style={{ color: tokens.colorStatusDangerForeground1 }}
                disabled={approveMut.isPending || rejectMut.isPending}
                onClick={() => { setError(''); setReason(''); setRejectTarget(e); }}
              >
                {t.expenseApprovals.reject}
              </Button>
            </div>
          </Card>
        ))
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(_, d) => { if (!d.open) setRejectTarget(null); }}>
        <DialogSurface style={{ maxWidth: '420px' }}>
          <DialogBody>
            <DialogTitle>{t.expenseApprovals.rejectTitle}</DialogTitle>
            <DialogContent>
              <Text block>{t.expenseApprovals.rejectBody}</Text>
              <Field label={t.expenseApprovals.reasonLabel} style={{ marginTop: tokens.spacingVerticalM }}>
                <Textarea
                  value={reason}
                  maxLength={500}
                  onChange={(_, d) => setReason(d.value)}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="subtle" onClick={() => setRejectTarget(null)}>{t.expenseApprovals.cancel}</Button>
              <Button
                appearance="primary"
                disabled={rejectMut.isPending}
                onClick={() => rejectTarget && rejectMut.mutate({ expenseId: rejectTarget.expenseId, why: reason.trim() })}
              >
                {rejectMut.isPending ? <Spinner size="tiny" /> : t.expenseApprovals.reject}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
